import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { AVAILABLE_SKINS, DEFAULT_SKIN_ID, isValidSkin } from '../skins/tokens';
import { empresaAPI } from '../services/empresaAPI';

const STORAGE_KEY = 'ibc_active_skin';
const SkinContext = createContext(null);

/**
 * Inyecta un <link rel="stylesheet"> con las Google Fonts de la skin.
 * Idempotente por href.
 */
const ensureFontsLoaded = (href) => {
  if (!href) return;
  if (document.querySelector(`link[data-skin-fonts][href="${href}"]`)) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  link.setAttribute('data-skin-fonts', 'true');
  document.head.appendChild(link);
};

const applySkinToDom = (skinId) => {
  const skin = AVAILABLE_SKINS.find((s) => s.id === skinId) || AVAILABLE_SKINS[0];
  document.documentElement.setAttribute('data-skin', skin.id);
  ensureFontsLoaded(skin.fontsHref);
};

const readStoredSkin = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw && isValidSkin(raw)) return raw;
  } catch (_) { /* ignore */ }
  return null;
};

const writeStoredSkin = (id) => {
  try { localStorage.setItem(STORAGE_KEY, id); } catch (_) { /* ignore */ }
};

/**
 * Provider que aplica el skin activo al <html> mediante data-skin.
 *
 * Prioridad de resolución al montar:
 *  1. Valor persistido en localStorage (respuesta instantánea, sin flash).
 *  2. Fetch a GET /api/empresa/config (fuente de verdad de la organización).
 *  3. DEFAULT_SKIN_ID como último fallback.
 */
export const SkinProvider = ({ children, defaultSkin = DEFAULT_SKIN_ID }) => {
  const [skinId, setSkinId] = useState(() => readStoredSkin() || defaultSkin);
  const [loading, setLoading] = useState(true);

  // Aplicar inmediatamente al montar (sin esperar al backend)
  useEffect(() => {
    applySkinToDom(skinId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reconciliar con backend — solo aplicar si viene realmente persistido
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await empresaAPI.getConfig();
        const remote = data?.skin_activo;
        const persisted = data?.persisted !== false;
        // Solo respetar el remoto si está persistido en BD; si es fallback
        // en memoria, mantenemos la elección local del usuario.
        if (!cancelled && persisted && remote && isValidSkin(remote) && remote !== skinId) {
          setSkinId(remote);
          writeStoredSkin(remote);
          applySkinToDom(remote);
        }
      } catch (_) {
        // Silencioso: si el backend no responde, seguimos con el skin local
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeSkin = useCallback(async (nextId, { persist = true } = {}) => {
    if (!isValidSkin(nextId)) return { ok: false, persisted: false };
    setSkinId(nextId);
    writeStoredSkin(nextId);
    applySkinToDom(nextId);
    if (persist) {
      try {
        const { data } = await empresaAPI.updateConfig({ skin_activo: nextId });
        return { ok: true, persisted: data?.persisted !== false };
      } catch (e) {
        return { ok: false, persisted: false };
      }
    }
    return { ok: true, persisted: false };
  }, []);

  return (
    <SkinContext.Provider value={{ skinId, changeSkin, loading, available: AVAILABLE_SKINS }}>
      {children}
    </SkinContext.Provider>
  );
};

export const useSkin = () => {
  const ctx = useContext(SkinContext);
  if (!ctx) throw new Error('useSkin debe usarse dentro de <SkinProvider>');
  return ctx;
};

export default SkinContext;
